(() => {
    "use strict";

    window.lvdc = window.lvdc || {};

    window.lvdc.ProductGrid = class extends HTMLElement {
        connectedCallback() {
            // Helpers & flags
            this.carryKeys = ["sort_by", "view"]; // + prefixes filter./constraint
            this.loading = false;

            // Sélecteurs & cache
            this.productGridSelector = "#items-grid";
            this.loadMoreButtonSelector = ".click-to-load";
            this.loadMoreButtonTextSelector = ".product-count";
            this.loadMoreButtonProductCountSelector = ".product-count span";
            this.productCardSelector = ".card-product";
            this.promotionCardSelector = ".promotion-block";
            this.cache = new window.lvdc.CacheManager();

            // Bind
            this.onSeeMoreClick = this.onSeeMoreClick.bind(this);
            this.onSectionLoad = this.onSectionLoad.bind(this);
            this.onEurusAppended = this.onEurusAppended.bind(this);

            // Initialisation DOM + listeners
            this.rebind();

            // Shopify / thème rerend la section (tri/filtre)
            document.addEventListener("shopify:section:load", this.onSectionLoad);
            document.addEventListener("Eurus:ajax:refresh", this.onSectionLoad);

            // Eurus (pagination.js) nous signale qu’une page a été ajoutée
            window.addEventListener("eurus:page-appended", this.onEurusAppended);
        }

        disconnectedCallback() {
            document.removeEventListener("shopify:section:load", this.onSectionLoad);
            document.removeEventListener("Eurus:ajax:refresh", this.onSectionLoad);
            window.removeEventListener("eurus:page-appended", this.onEurusAppended);
            this.seeMoreButton?.removeEventListener("click", this.onSeeMoreClick);
        }

        /* -------------------------
         * Getters DOM (avec cache)
         * ------------------------- */
        get productGrid() {
            return this.cache.get("dom", "productGrid", () => {
                return document.querySelector(this.productGridSelector);
            });
        }

        get seeMoreButton() {
            return this.cache.get("dom", "seeMoreButton", () => {
                return document.querySelector(this.loadMoreButtonSelector);
            });
        }

        get seeMoreButtonText() {
            return this.cache.get("dom", "seeMoreButtonText", () => {
                return document.querySelector(this.loadMoreButtonTextSelector);
            });
        }

        get productShownText() {
            return this.cache.get("dom", "shownProductCount", () => {
                return this.querySelector(this.loadMoreButtonProductCountSelector);
            });
        }

        get pageNumber() {
            return this.productGrid?.dataset?.pageNumber;
        }

        get productShownCount() {
            return this.productGrid?.dataset?.productShownCount;
        }

        /* -------------------------
         * Rebind après rerender
         * ------------------------- */
        rebind() {
            // Invalide le cache DOM
            this.cache?.delete?.("dom", "productGrid");
            this.cache?.delete?.("dom", "seeMoreButton");
            this.cache?.delete?.("dom", "seeMoreButtonText");
            this.cache?.delete?.("dom", "shownProductCount");

            // Débinde / rebinde le click (SEULEMENT si le bouton n’est pas géré par Alpine)
            this.seeMoreButton?.removeEventListener("click", this.onSeeMoreClick);
            if (this.seeMoreButton && this.seeMoreButton.dataset.managedBy !== "xPagination") {
                this.seeMoreButton.addEventListener("click", this.onSeeMoreClick, { passive: true });
            }

            // Mets à jour le compteur d’affichage
            if (this.productGrid) {
                this.updateProductCountText(this.productGrid);
            }
        }

        onSectionLoad() {
            // La section a été rerendue → reselect + rebind
            this.rebind();
        }

        onEurusAppended(e) {
            // Une page vient d’être ajoutée par Alpine/Eurus :
            // 1) recalculer les compteurs, 2) rebind sur le nouveau bouton (sans ajouter notre click si managedBy)
            this.updateProductCountText(this.productGrid);
            this.rebind();
        }

        /* -------------------------
         * Construction de l’URL page suivante
         * (utile si LVDC pilote seul sur d’autres templates)
         * ------------------------- */
        get endpoint() {
            // Base : l’URL fournie par Liquid dans data-next-url (prioritaire)
            const base = this.seeMoreButton?.dataset?.nextUrl || window.location.href;
            const nextUrl = new URL(base, window.location.origin);

            // Porte les paramètres de la page courante si absents
            const current = new URL(window.location.href);
            current.searchParams.forEach((val, key) => {
                const isFacet =
                    this.carryKeys.includes(key) ||
                    key.startsWith("filter.") ||
                    key.startsWith("constraint");
                if (isFacet && !nextUrl.searchParams.has(key)) {
                    nextUrl.searchParams.set(key, val);
                }
            });

            // Si pas de ?page explicite, force la suivante depuis data-page-number
            if (!nextUrl.searchParams.has("page") && this.pageNumber !== "last") {
                nextUrl.searchParams.set("page", Number(this.pageNumber) + 1);
            }

            return nextUrl.toString();
        }

        /* -------------------------
         * Handler "Voir plus" (utilisé seulement si pas géré par Alpine)
         * ------------------------- */
        onSeeMoreClick = async (event) => {
            if (this.loading || window.__eurusLoading) return;
            // Évite qu’un éventuel autre listener sur le même bouton parte aussi
            event.stopImmediatePropagation?.();
            event.preventDefault?.();

            try {
                this.enableLoading();

                const response = await fetch(this.endpoint);
                if (!response.ok) throw new Error("Network response was not ok");

                const nextPageMarkup = await response.text();
                const nextPageHTML = new DOMParser().parseFromString(
                    nextPageMarkup,
                    "text/html"
                );

                const nextProductList = nextPageHTML.querySelector(this.productGridSelector);
                if (!nextProductList)
                    throw new Error("Could not find product list in the fetched page");

                // Append uniquement les éléments attendus (cards + promos)
                const productHTMLList = Array.from(nextProductList.children)
                    .filter((el) => el.matches?.(".card-product, .promotion-block"))
                    .map((child) => child.outerHTML)
                    .join("");

                this.productGrid.insertAdjacentHTML("beforeend", productHTMLList);

                // Met à jour les compteurs & statut "last"
                this.updateProgress(nextPageHTML);

                // Met à jour la zone bouton/compteur depuis la page suivante
                this.updateLoadMoreUi(nextPageHTML);

                // Rebrancher l’event sur le nouveau bouton injecté
                this.rebind();
            } catch (error) {
                console.error("Error loading more products", error);
            } finally {
                this.disableLoading();
            }
        };

        /* -------------------------
         * Mises à jour d’état UI
         * ------------------------- */
        updateProgress(nextPageHTML) {
            // Passe à la page suivante
            this.productGrid.dataset.pageNumber = Number(this.pageNumber) + 1;

            // Si la page suivante marque "last", synchronise l’état et retire le bouton
            const nextList = nextPageHTML.querySelector(this.productGridSelector);
            if (nextList?.dataset?.pageNumber === "last") {
                this.productGrid.dataset.pageNumber = "last";
                this.seeMoreButton?.remove();
            }

            // Met à jour le texte “X sur total”
            this.updateProductCountText(this.productGrid);
        }

        updateLoadMoreUi(nextPageHTML) {
            // Récupère le conteneur bouton/compteur de la section courante
            const sectionId =
                this.closest('[id^="shopify-section-"]')?.id?.replace("shopify-section-", "") || "";
            const dest =
                document.getElementById(`btn-pagination-${sectionId}`) ||
                document.getElementById("btn-pagination-{{ section.id }}"); // fallback si rendu statique
            if (!dest) return;

            // Récupère le même conteneur depuis la page suivante
            const src = nextPageHTML.getElementById(dest.id);
            if (src) {
                dest.innerHTML = src.innerHTML;
            }
        }

        updateProductCountText() {
            // Compte réel dans le DOM courant
            const productCount = this.productGrid.querySelectorAll(this.productCardSelector).length;
            const promotionCardCount = this.productGrid.querySelectorAll(this.promotionCardSelector).length;

            this.productGrid.dataset.promotionCardCount = promotionCardCount;
            this.productGrid.dataset.productShownCount = productCount - promotionCardCount;

            const seeMoreButtonTextContent = this.seeMoreButtonText?.dataset?.text;
            const span = this.seeMoreButtonText?.querySelector("span");

            if (seeMoreButtonTextContent && span) {
                span.innerHTML = seeMoreButtonTextContent.replace(
                    "%count%",
                    productCount - promotionCardCount
                );
            }
        }

        enableLoading() {
            this.loading = true;
            this.seeMoreButton?.classList.add("pointer-events-none");
            this.seeMoreButton?.classList.add("btn--loading");
        }

        disableLoading() {
            this.loading = false;
            this.seeMoreButton?.classList.remove("pointer-events-none");
            this.seeMoreButton?.classList.remove("btn--loading");
        }
    };

    if (!window.customElements.get("lvdc-product-grid")) {
        window.customElements.define("lvdc-product-grid", window.lvdc.ProductGrid);
    }
})();
